import axios from 'axios';
import { getToken } from './tokenStorage';

export const resolveBackendBaseUrl = (configuredUrl) => {
  const fallbackUrl = typeof window !== 'undefined' ? window.location?.origin : '';
  const rawUrl = `${configuredUrl || fallbackUrl || ''}`.trim().replace(/\/+$/, '');
  if (!rawUrl || rawUrl === 'undefined' || rawUrl === 'null') return '';
  return rawUrl.replace(/\/admin(?:\/.*)?$/i, '').replace(/\/api(?:\/.*)?$/i, '');
};

const BACKEND_URL = resolveBackendBaseUrl(process.env.REACT_APP_BACKEND_URL);
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Helper to resolve image URLs - handles relative /api/ paths
export const getImageUrl = (url) => {
  if (!url) return null;
  // If it's already a full URL (http/https), return as-is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  // If it's a relative API path, prefix with backend URL
  if (url.startsWith('/api/')) {
    return `${BACKEND_URL}${url}`;
  }
  // Otherwise return as-is
  return url;
};

export const api = {
  // Dashboard
  getDashboard: () => axios.get(`${API}/dashboard`, { headers: getAuthHeader() }),
  getStats: () => axios.get(`${API}/stats`, { headers: getAuthHeader() }),
  getOrganizations: () => axios.get(`${API}/organizations`, { headers: getAuthHeader() }),
  getOrganizationDashboard: (organizationId) => axios.get(`${API}/organizations/${organizationId}/dashboard`, { headers: getAuthHeader() }),
  deleteAccount: (password) => axios.post(`${API}/auth/delete-account`, { password }, { headers: getAuthHeader() }),

  // Home Feed (private, authenticated)
  getHomeFeed: (params) => axios.get(`${API}/feed`, { headers: getAuthHeader(), params }),
  
  // Memories
  getMemories: (params) => axios.get(`${API}/memories`, { headers: getAuthHeader(), params }),
  getMemory: (id) => axios.get(`${API}/memories/${id}`, { headers: getAuthHeader() }),
  getAdjacentMemories: (id) => axios.get(`${API}/memories/${id}/adjacent`, { headers: getAuthHeader() }),
  createMemory: (data) => axios.post(`${API}/memories`, data, { headers: getAuthHeader() }),
  deleteMemory: (id) => axios.delete(`${API}/memories/${id}`, { headers: getAuthHeader() }),
  bulkDeleteMemories: (ids) => axios.post(`${API}/memories/bulk-delete`, ids, { headers: getAuthHeader() }),
  uploadPhoto: (formData) => axios.post(`${API}/memories/upload`, formData, {
    headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
  }),
  uploadPhotosBatch: (formData) => axios.post(`${API}/memories/upload-batch`, formData, {
    headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
  }),
  
  // Events
  getEvents: (params) => axios.get(`${API}/events`, { headers: getAuthHeader(), params }),
  getEvent: (id) => axios.get(`${API}/events/${id}`, { headers: getAuthHeader() }),
  createEvent: (data) => axios.post(`${API}/events`, data, { headers: getAuthHeader() }),
  updateEvent: (id, data) => axios.put(`${API}/events/${id}`, data, { headers: getAuthHeader() }),
  
  // Life Chapters (Full CRUD System)
  getLifeChapters: () => axios.get(`${API}/life-chapters`, { headers: getAuthHeader() }),
  getLifeChapter: (id) => axios.get(`${API}/life-chapters/${id}`, { headers: getAuthHeader() }),
  createLifeChapter: (data) => axios.post(`${API}/life-chapters`, data, { headers: getAuthHeader() }),
  updateLifeChapter: (id, data) => axios.put(`${API}/life-chapters/${id}`, data, { headers: getAuthHeader() }),
  deleteLifeChapter: (id) => axios.delete(`${API}/life-chapters/${id}`, { headers: getAuthHeader() }),

  // ============== Resurface & Recap ==============
  getDailyResurface: () => axios.get(`${API}/resurface/daily`, { headers: getAuthHeader() }),
  dismissDailyResurface: () => axios.post(`${API}/resurface/daily/dismiss`, {}, { headers: getAuthHeader() }),
  getWeeklyRecap: () => axios.get(`${API}/resurface/weekly`, { headers: getAuthHeader() }),
  getResurfacePreferences: () => axios.get(`${API}/resurface/preferences`, { headers: getAuthHeader() }),
  updateResurfacePreferences: (data) => axios.put(`${API}/resurface/preferences`, data, { headers: getAuthHeader() }),
  hideResurfacedMemory: (memoryId) => axios.post(`${API}/resurface/hide`, { memory_id: memoryId }, { headers: getAuthHeader() }),

  // ============== Relationship Graph ==============
  getRelationshipGraph: () => axios.get(`${API}/relationship-graph`, { headers: getAuthHeader() }),
  getGraphPerson: (personId) => axios.get(`${API}/relationship-graph/person/${personId}`, { headers: getAuthHeader() }),
  getSharedMemories: (personA, personB, limit = 20) => axios.get(`${API}/relationship-graph/shared-memories`, { headers: getAuthHeader(), params: { person_a: personA, person_b: personB, limit } }),
  reorderLifeChapters: (orders) => axios.put(`${API}/life-chapters/reorder`, { orders }, { headers: getAuthHeader() }),
  getLifeChaptersStats: () => axios.get(`${API}/life-chapters/stats`, { headers: getAuthHeader() }),
  getChapterInsights: (id) => axios.get(`${API}/life-chapters/${id}/insights`, { headers: getAuthHeader() }),
  addMomentsToChapter: (id, momentIds) => axios.post(`${API}/life-chapters/${id}/moments`, momentIds, { headers: getAuthHeader() }),
  removeMomentsFromChapter: (id, momentIds) => axios.delete(`${API}/life-chapters/${id}/moments`, { headers: getAuthHeader(), data: momentIds }),
  addMediaToChapter: (id, mediaIds) => axios.post(`${API}/life-chapters/${id}/media`, mediaIds, { headers: getAuthHeader() }),
  removeMediaFromChapter: (id, mediaIds) => axios.delete(`${API}/life-chapters/${id}/media`, { headers: getAuthHeader(), data: mediaIds }),
  suggestLifeChapters: () => axios.post(`${API}/life-chapters/suggest`, {}, { headers: getAuthHeader() }),
  acceptChapterSuggestion: (index, title, description) => axios.post(`${API}/life-chapters/suggest/${index}/accept`, { title, description }, { headers: getAuthHeader() }),
  getChaptersTimeline: () => axios.get(`${API}/life-chapters/timeline-view`, { headers: getAuthHeader() }),
  generateChapterNarrative: (id) => axios.post(`${API}/life-chapters/${id}/generate-narrative`, {}, { headers: getAuthHeader() }),
  
  // Timeline
  getTimeline: (params) => axios.get(`${API}/timeline`, { headers: getAuthHeader(), params }),
  getTimelineYear: (year) => axios.get(`${API}/timeline/year/${year}`, { headers: getAuthHeader() }),
  getUnifiedTimeline: (params) => axios.get(`${API}/timeline/unified`, { headers: getAuthHeader(), params }),
  
  // Discovery
  getDiscovery: () => axios.get(`${API}/discovery`, { headers: getAuthHeader() }),
  
  // Projects
  getProjects: () => axios.get(`${API}/projects`, { headers: getAuthHeader() }),
  getProject: (id) => axios.get(`${API}/projects/${id}`, { headers: getAuthHeader() }),
  createProject: (data) => axios.post(`${API}/projects`, data, { headers: getAuthHeader() }),
  updateProject: (id, data) => axios.put(`${API}/projects/${id}`, data, { headers: getAuthHeader() }),
  inviteToProject: (id, data) => axios.post(`${API}/projects/${id}/invite`, data, { headers: getAuthHeader() }),
  addMemoriesToProject: (id, memoryIds) => axios.post(`${API}/projects/${id}/add-memories`, { memory_ids: memoryIds }, { headers: getAuthHeader() }),
  
  // Books
  getBooks: () => axios.get(`${API}/books`, { headers: getAuthHeader() }),
  getBook: (id) => axios.get(`${API}/books/${id}`, { headers: getAuthHeader() }),
  createBook: (data) => axios.post(`${API}/books`, data, { headers: getAuthHeader() }),
  updateBook: (id, data) => axios.put(`${API}/books/${id}`, data, { headers: getAuthHeader() }),
  deleteBook: (id) => axios.delete(`${API}/books/${id}`, { headers: getAuthHeader() }),

  // Black-page print proof: preparation, status and owner-authenticated downloads
  prepareBookPrintProof: (id, body) =>
    axios.post(`${API}/books/${id}/editor/prepare-print`, body, {
      headers: { ...getAuthHeader(), Prefer: 'respond-async' },
    }),
  getBookPrintProofStatus: (id, jobId) =>
    axios.get(`${API}/books/${id}/editor/prepare-print-status`, {
      headers: getAuthHeader(),
      params: jobId ? { job_id: jobId } : undefined,
    }),
  downloadBookPrintArtifact: (id, kind) =>
    axios.get(`${API}/books/${id}/editor/print-artifacts/${kind}`, {
      headers: getAuthHeader(),
      responseType: 'blob',
    }),
  generateBookPreview: (id) => axios.post(`${API}/books/${id}/generate-preview`, {}, { headers: getAuthHeader() }),
  getBookEventSuggestions: () => axios.get(`${API}/books/suggestions/events`, { headers: getAuthHeader() }),
  
  // Legacy Vault
  // ============== Living Legacies ==============
  getLegacies: (params) => axios.get(`${API}/legacies`, { headers: getAuthHeader(), params }),
  getLegacy: (id) => axios.get(`${API}/legacies/${id}`, { headers: getAuthHeader() }),
  createLegacy: (data) => axios.post(`${API}/legacies`, data, { headers: getAuthHeader() }),
  updateLegacy: (id, data) => axios.put(`${API}/legacies/${id}`, data, { headers: getAuthHeader() }),
  deleteLegacy: (id) => axios.delete(`${API}/legacies/${id}`, { headers: getAuthHeader() }),
  generateLegacySummary: (id) => axios.post(`${API}/legacies/${id}/ai-summary`, {}, { headers: getAuthHeader() }),
  shareLegacy: (id) => axios.post(`${API}/legacies/${id}/share`, {}, { headers: getAuthHeader() }),
  getSharedLegacy: (slug) => axios.get(`${API}/legacies/shared/${slug}`),

  // Reflection Prompts
  getPrompts: (limit) => axios.get(`${API}/prompts`, { headers: getAuthHeader(), params: limit ? { limit } : {} }),
  dismissPrompt: (promptId) => axios.post(`${API}/prompts/${promptId}/dismiss`, {}, { headers: getAuthHeader() }),
  clearDismissedPrompts: () => axios.delete(`${API}/prompts/dismissed`, { headers: getAuthHeader() }),
  getLegacyTimelineMarkers: () => axios.get(`${API}/legacies/timeline/markers`, { headers: getAuthHeader() }),

  // Legacy Vault (older)
  getLegacyVault: () => axios.get(`${API}/legacy-vault`, { headers: getAuthHeader() }),
  createLegacyEntry: (data) => axios.post(`${API}/legacy-vault/entries`, data, { headers: getAuthHeader() }),
  updateLegacyEntry: (id, data) => axios.put(`${API}/legacy-vault/entries/${id}`, data, { headers: getAuthHeader() }),
  deleteLegacyEntry: (id) => axios.delete(`${API}/legacy-vault/entries/${id}`, { headers: getAuthHeader() }),
  createFutureLetter: (data) => axios.post(`${API}/legacy-vault/future-letters`, data, { headers: getAuthHeader() }),
  getFutureLetter: (id) => axios.get(`${API}/legacy-vault/future-letters/${id}`, { headers: getAuthHeader() }),
  updateFutureLetter: (id, data) => axios.put(`${API}/legacy-vault/future-letters/${id}`, data, { headers: getAuthHeader() }),
  deleteFutureLetter: (id) => axios.delete(`${API}/legacy-vault/future-letters/${id}`, { headers: getAuthHeader() }),
  unlockFutureLetter: (id) => axios.post(`${API}/legacy-vault/future-letters/${id}/unlock`, {}, { headers: getAuthHeader() }),
  
  // AI Companion
  aiChat: (data) => axios.post(`${API}/ai-companion/chat`, data, { headers: getAuthHeader() }),
  aiGenerateStory: (eventId) => axios.post(`${API}/ai-companion/generate-story?event_id=${eventId}`, {}, { headers: getAuthHeader() }),
  
  // Life Graph
  getLifeGraph: () => axios.get(`${API}/life-graph`, { headers: getAuthHeader() }),
  getGraphConnections: () => axios.get(`${API}/life-graph/connections`, { headers: getAuthHeader() }),
  getGraphPeople: () => axios.get(`${API}/life-graph/people`, { headers: getAuthHeader() }),
  createGraphPerson: (data) => axios.post(`${API}/life-graph/people`, data, { headers: getAuthHeader() }),
  updateGraphPerson: (id, data) => axios.put(`${API}/life-graph/people/${id}`, data, { headers: getAuthHeader() }),
  deleteGraphPerson: (id) => axios.delete(`${API}/life-graph/people/${id}`, { headers: getAuthHeader() }),
  getGraphLocations: () => axios.get(`${API}/life-graph/locations`, { headers: getAuthHeader() }),
  createGraphLocation: (data) => axios.post(`${API}/life-graph/locations`, data, { headers: getAuthHeader() }),
  
  // AI Search
  aiSearch: (query, limit = 20) => axios.post(`${API}/search/ai`, { query, limit }, { headers: getAuthHeader() }),
  quickSearch: (q, limit = 10) => axios.get(`${API}/search/quick`, { headers: getAuthHeader(), params: { q, limit } }),
  
  // Real-time Collaboration
  getOnlineUsers: (projectId) => axios.get(`${API}/projects/${projectId}/online-users`, { headers: getAuthHeader() }),
  
  // Insights
  getInsights: () => axios.get(`${API}/insights`, { headers: getAuthHeader() }),
  generateInsights: () => axios.post(`${API}/insights/generate`, {}, { headers: getAuthHeader() }),
  
  // Voice Memories
  getVoiceMemories: (params) => axios.get(`${API}/voice-memories`, { headers: getAuthHeader(), params }),
  getVoiceMemory: (id) => axios.get(`${API}/voice-memories/${id}`, { headers: getAuthHeader() }),
  uploadVoiceMemory: (formData) => axios.post(`${API}/voice-memories/upload`, formData, {
    headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
  }),
  updateVoiceMemory: (id, data) => axios.put(`${API}/voice-memories/${id}`, data, { headers: getAuthHeader() }),
  deleteVoiceMemory: (id) => axios.delete(`${API}/voice-memories/${id}`, { headers: getAuthHeader() }),
  
  // AI Tagging
  suggestTags: (data) => axios.post(`${API}/ai/suggest-tags`, data, { headers: getAuthHeader() }),
  applyTags: (memoryId, data) => axios.post(`${API}/ai/apply-tags/${memoryId}`, data, { headers: getAuthHeader() }),
  autoTagAll: (limit) => axios.post(`${API}/ai/auto-tag-all?limit=${limit || 10}`, {}, { headers: getAuthHeader() }),
  
  // Memory Updates
  updateMemory: (id, data) => axios.put(`${API}/memories/${id}`, data, { headers: getAuthHeader() }),
  
  // Legacy compatibility
  getChapters: () => axios.get(`${API}/chapters`, { headers: getAuthHeader() }),
  getChapter: (id) => axios.get(`${API}/chapters/${id}`, { headers: getAuthHeader() }),
  getCollaborators: () => axios.get(`${API}/collaborators`, { headers: getAuthHeader() }),
  getActivity: () => axios.get(`${API}/activity`, { headers: getAuthHeader() }),
  
  // Seed
  seedData: () => axios.post(`${API}/seed-data`, {}, { headers: getAuthHeader() }),
  
  // AI (legacy)
  generateStory: (chapterId) => axios.post(`${API}/ai/generate-story`, { chapter_id: chapterId }, { headers: getAuthHeader() }),
  
  // Collaborators (legacy)
  inviteCollaborator: (data) => axios.post(`${API}/collaborators`, data, { headers: getAuthHeader() }),

  // ============================================
  // ADMIN PLATFORM APIs
  // ============================================
  
  // Admin Auth
  adminLogin: (username, password) => axios.post(`${API}/admin/auth/login`, { username, password }),
  adminLogout: (token) => axios.post(`${API}/admin/auth/logout`, {}, { headers: { Authorization: `Bearer ${token}` } }),
  adminGetMe: (token) => axios.get(`${API}/admin/auth/me`, { headers: { Authorization: `Bearer ${token}` } }),
  adminChangePassword: (token, currentPassword, newPassword) => axios.post(`${API}/admin/auth/change-password`, 
    { current_password: currentPassword, new_password: newPassword },
    { headers: { Authorization: `Bearer ${token}` } }
  ),
  adminCreateEverBookNews: (token, data) => axios.post(`${API}/admin/everbook-news`, data, { headers: { Authorization: `Bearer ${token}` } }),
  adminUpdateEverBookNews: (token, postId, data) => axios.patch(`${API}/admin/everbook-news/${postId}`, data, { headers: { Authorization: `Bearer ${token}` } }),
  adminDeleteEverBookNews: (token, postId) => axios.delete(`${API}/admin/everbook-news/${postId}`, { headers: { Authorization: `Bearer ${token}` } }),
  getEverBookNews: (params) => axios.get(`${API}/social-feed/everbook-news`, { params }),
  adminGetDiscoverContent: (token, params) => axios.get(`${API}/admin/discover-content`, { headers: { Authorization: `Bearer ${token}` }, params }),
  adminCreateDiscoverContent: (token, data) => axios.post(`${API}/admin/discover-content`, data, { headers: { Authorization: `Bearer ${token}` } }),
  adminGetDiscoverContentItem: (token, contentId) => axios.get(`${API}/admin/discover-content/${contentId}`, { headers: { Authorization: `Bearer ${token}` } }),
  adminUpdateDiscoverContent: (token, contentId, data) => axios.patch(`${API}/admin/discover-content/${contentId}`, data, { headers: { Authorization: `Bearer ${token}` } }),
  adminDiscoverContentAction: (token, contentId, action) => axios.post(`${API}/admin/discover-content/${contentId}/action`, { action }, { headers: { Authorization: `Bearer ${token}` } }),
  adminDuplicateDiscoverContent: (token, contentId) => axios.post(`${API}/admin/discover-content/${contentId}/duplicate`, {}, { headers: { Authorization: `Bearer ${token}` } }),
  adminReorderDiscoverContent: (token, data) => axios.post(`${API}/admin/discover-content/reorder`, data, { headers: { Authorization: `Bearer ${token}` } }),
  adminGetDiscoverOfferCandidates: (token, params) => axios.get(`${API}/admin/discover-offers/eligible`, { headers: { Authorization: `Bearer ${token}` }, params }),
  adminGetDiscoverOfferPlacements: (token, params) => axios.get(`${API}/admin/discover-offers/placements`, { headers: { Authorization: `Bearer ${token}` }, params }),
  adminCreateDiscoverOfferPlacement: (token, data) => axios.post(`${API}/admin/discover-offers/placements`, data, { headers: { Authorization: `Bearer ${token}` } }),
  adminUpdateDiscoverOfferPlacement: (token, placementId, data) => axios.patch(`${API}/admin/discover-offers/placements/${placementId}`, data, { headers: { Authorization: `Bearer ${token}` } }),
  adminUploadDiscoverMedia: (token, file, kind) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/admin/discover-content/upload-media`, formData, {
      headers: { Authorization: `Bearer ${token}` },
      params: { kind },
    });
  },
  getDiscoverOverview: () => axios.get(`${API}/discover`),
  getDiscoverSection: (section, params) => axios.get(`${API}/discover/sections/${section}`, { params }),
  getDiscoverContent: (contentId) => axios.get(`${API}/discover/content/${contentId}`),
  getDiscoverOffers: (params) => axios.get(`${API}/discover/offers`, { params }),

  // Organization workspace
  createEchoOffer: (data) => axios.post(`${API}/echo/offers`, data, { headers: getAuthHeader() }),
  updateEchoOffer: (offerId, data) => axios.patch(`${API}/echo/offers/${offerId}`, data, { headers: getAuthHeader() }),
  getOrganizationSponsoredCampaignPackages: (organizationId) => axios.get(`${API}/organizations/${organizationId}/sponsored-campaign-packages`, { headers: getAuthHeader() }),
  getOrganizationSponsoredCampaigns: (organizationId) => axios.get(`${API}/organizations/${organizationId}/sponsored-campaigns`, { headers: getAuthHeader() }),
  createOrganizationSponsoredCampaign: (organizationId, data) => axios.post(`${API}/organizations/${organizationId}/sponsored-campaigns`, data, { headers: getAuthHeader() }),
  createSponsoredCampaignCheckout: (organizationId, campaignId, originUrl) => axios.post(`${API}/organizations/${organizationId}/sponsored-campaigns/${campaignId}/checkout`, { origin_url: originUrl }, { headers: getAuthHeader() }),
  reconcileSponsoredCampaignPayment: (organizationId, campaignId) => axios.post(`${API}/organizations/${organizationId}/sponsored-campaigns/${campaignId}/reconcile-payment`, {}, { headers: getAuthHeader() }),

  // Admin Users Management
  adminGetUsers: (token) => axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } }),
  adminCreateUser: (token, data) => axios.post(`${API}/admin/users`, data, { headers: { Authorization: `Bearer ${token}` } }),
  adminUpdateUser: (token, userId, data) => axios.put(`${API}/admin/users/${userId}`, data, { headers: { Authorization: `Bearer ${token}` } }),
  adminResetPassword: (token, userId) => axios.post(`${API}/admin/users/${userId}/reset-password`, {}, { headers: { Authorization: `Bearer ${token}` } }),
  adminDeactivateUser: (token, userId) => axios.delete(`${API}/admin/users/${userId}`, { headers: { Authorization: `Bearer ${token}` } }),
  adminGetLoginHistory: (token, userId) => axios.get(`${API}/admin/users/${userId}/login-history`, { headers: { Authorization: `Bearer ${token}` } }),

  // Admin App User Management
  adminGetAppUsers: (token, params) => axios.get(`${API}/admin/app-users`, { headers: { Authorization: `Bearer ${token}` }, params }),
  adminGetAppUserStats: (token) => axios.get(`${API}/admin/app-users/stats`, { headers: { Authorization: `Bearer ${token}` } }),
  adminGetAppUserDetail: (token, userId) => axios.get(`${API}/admin/app-users/${userId}`, { headers: { Authorization: `Bearer ${token}` } }),
  adminChangeUserPlan: (token, userId, data) => axios.put(`${API}/admin/app-users/${userId}/plan`, data, { headers: { Authorization: `Bearer ${token}` } }),
  adminSuspendUser: (token, userId, data) => axios.put(`${API}/admin/app-users/${userId}/suspend`, data, { headers: { Authorization: `Bearer ${token}` } }),
  adminResetStorage: (token, userId) => axios.put(`${API}/admin/app-users/${userId}/reset-storage`, {}, { headers: { Authorization: `Bearer ${token}` } }),
  adminGrantLifetime: (token, userId) => axios.put(`${API}/admin/app-users/${userId}/grant-lifetime`, {}, { headers: { Authorization: `Bearer ${token}` } }),

  // Admin Impersonation
  adminImpersonateUser: (token, userId) => axios.post(`${API}/admin/impersonate/${userId}`, {}, { headers: { Authorization: `Bearer ${token}` } }),
  adminEndImpersonation: (token) => axios.post(`${API}/admin/end-impersonation`, {}, { headers: { Authorization: `Bearer ${token}` } }),

  // Admin Activity Dashboard
  adminGetActivityMetrics: (token) => axios.get(`${API}/admin/activity/metrics`, { headers: { Authorization: `Bearer ${token}` } }),
  adminGetUserGrowth: (token, period) => axios.get(`${API}/admin/activity/user-growth`, { headers: { Authorization: `Bearer ${token}` }, params: { period } }),
  adminGetPlanDistribution: (token) => axios.get(`${API}/admin/activity/plan-distribution`, { headers: { Authorization: `Bearer ${token}` } }),
  adminGetActivityFeed: (token, params) => axios.get(`${API}/admin/activity/feed`, { headers: { Authorization: `Bearer ${token}` }, params }),
  adminGetUserPlanHistory: (token, userId) => axios.get(`${API}/admin/app-users/${userId}/plan-history`, { headers: { Authorization: `Bearer ${token}` } }),

  // Admin Analytics
  adminGetKPIs: (token) => axios.get(`${API}/admin/analytics/kpis`, { headers: { Authorization: `Bearer ${token}` } }),
  adminGetAnalyticsActivityFeed: (token, eventType) => axios.get(`${API}/admin/analytics/activity-feed`, { 
    headers: { Authorization: `Bearer ${token}` },
    params: eventType ? { event_type: eventType } : {}
  }),
  adminGetFunnel: (token) => axios.get(`${API}/admin/analytics/funnel`, { headers: { Authorization: `Bearer ${token}` } }),
  adminGetAIHealth: (token) => axios.get(`${API}/admin/analytics/ai-health`, { headers: { Authorization: `Bearer ${token}` } }),
  adminGetSystemHealth: (token) => axios.get(`${API}/admin/system/health`, { headers: { Authorization: `Bearer ${token}` } }),
  adminGetExecutiveOverview: (token) => axios.get(`${API}/admin/analytics/executive-overview`, { headers: { Authorization: `Bearer ${token}` } }),
  adminGetGrowth: (token) => axios.get(`${API}/admin/analytics/growth`, { headers: { Authorization: `Bearer ${token}` } }),
  adminGetUserBehavior: (token) => axios.get(`${API}/admin/analytics/user-behavior`, { headers: { Authorization: `Bearer ${token}` } }),
  adminGetBookAnalytics: (token) => axios.get(`${API}/admin/analytics/books`, { headers: { Authorization: `Bearer ${token}` } }),
  adminGetOperations: (token) => axios.get(`${API}/admin/analytics/operations`, { headers: { Authorization: `Bearer ${token}` } }),
  adminGetRetention: (token) => axios.get(`${API}/admin/analytics/retention`, { headers: { Authorization: `Bearer ${token}` } }),

  // Admin Alerts
  adminGetAlerts: (token, params) => axios.get(`${API}/admin/alerts`, { headers: { Authorization: `Bearer ${token}` }, params }),
  adminResolveAlert: (token, alertId) => axios.post(`${API}/admin/alerts/${alertId}/resolve`, {}, { headers: { Authorization: `Bearer ${token}` } }),

  // Admin Audit Logs
  adminGetAuditLogs: (token, params) => axios.get(`${API}/admin/audit-logs`, { headers: { Authorization: `Bearer ${token}` }, params }),

  // Admin Export
  adminExportAnalytics: (token, reportType, format) => axios.get(`${API}/admin/export/analytics`, {
    headers: { Authorization: `Bearer ${token}` },
    params: { report_type: reportType, format }
  }),

  // Admin Pricing Management
  adminGetPricingPlans: (token) => axios.get(`${API}/admin/pricing/plans`, { headers: { Authorization: `Bearer ${token}` } }),
  adminGetPricingPlan: (token, planId) => axios.get(`${API}/admin/pricing/plans/${planId}`, { headers: { Authorization: `Bearer ${token}` } }),
  adminUpdatePricingPlan: (token, planId, data) => axios.put(`${API}/admin/pricing/plans/${planId}`, data, { headers: { Authorization: `Bearer ${token}` } }),
  adminCreatePricingPlan: (token, data) => axios.post(`${API}/admin/pricing/plans`, data, { headers: { Authorization: `Bearer ${token}` } }),
  adminArchivePricingPlan: (token, planId) => axios.delete(`${API}/admin/pricing/plans/${planId}`, { headers: { Authorization: `Bearer ${token}` } }),
  adminReorderPricingPlans: (token, order) => axios.put(`${API}/admin/pricing/reorder`, order, { headers: { Authorization: `Bearer ${token}` } }),
  adminGetPricingAnalytics: (token, days) => axios.get(`${API}/admin/pricing/analytics`, { headers: { Authorization: `Bearer ${token}` }, params: { days } }),
  adminGetFeatureKeys: (token) => axios.get(`${API}/admin/pricing/features`, { headers: { Authorization: `Bearer ${token}` } }),

  // Public Pricing
  getPricingPlans: () => axios.get(`${API}/pricing/plans`),
  trackPricingEvent: (event) => axios.post(`${API}/pricing/analytics/track`, event),

  // Admin Notifications
  adminGetNotifications: (token, limit = 20, unreadOnly = false) => axios.get(`${API}/admin/notifications`, {
    headers: { Authorization: `Bearer ${token}` },
    params: { limit, unread_only: unreadOnly }
  }),
  adminMarkNotificationRead: (token, notificationId) => axios.post(`${API}/admin/notifications/${notificationId}/read`, {}, {
    headers: { Authorization: `Bearer ${token}` }
  }),
  adminMarkAllNotificationsRead: (token) => axios.post(`${API}/admin/notifications/read-all`, {}, {
    headers: { Authorization: `Bearer ${token}` }
  }),
  adminDeleteNotification: (token, notificationId) => axios.delete(`${API}/admin/notifications/${notificationId}`, {
    headers: { Authorization: `Bearer ${token}` }
  }),
  adminGenerateSampleNotifications: (token) => axios.get(`${API}/admin/notifications/generate-sample`, {
    headers: { Authorization: `Bearer ${token}` }
  }),
  adminCheckTriggers: (token) => axios.get(`${API}/admin/notifications/check-triggers`, {
    headers: { Authorization: `Bearer ${token}` }
  }),
  adminGetTriggerStatus: (token) => axios.get(`${API}/admin/notifications/trigger-status`, {
    headers: { Authorization: `Bearer ${token}` }
  }),
  adminResetTrigger: (token, triggerName) => axios.post(`${API}/admin/notifications/reset-trigger/${triggerName}`, {}, {
    headers: { Authorization: `Bearer ${token}` }
  }),

  // ============================================
  // ADVANCED AI APIs (Phase 1)
  // ============================================
  
  // Timeline Intelligence
  clusterMemories: (forceRecluster = false) => axios.post(`${API}/ai-advanced/timeline/cluster`, 
    { force_recluster: forceRecluster }, { headers: getAuthHeader() }),
  getIntelligentTimeline: (year) => axios.get(`${API}/ai-advanced/timeline/intelligent`, 
    { headers: getAuthHeader(), params: year ? { year } : {} }),
  getTimelineYears: () => axios.get(`${API}/ai-advanced/timeline/years`, { headers: getAuthHeader() }),
  
  // Memory Curation
  scoreAllMemories: () => axios.post(`${API}/ai-advanced/curation/score-all`, {}, { headers: getAuthHeader() }),
  getHighlights: (params) => axios.get(`${API}/ai-advanced/curation/highlights`, { headers: getAuthHeader(), params }),
  getCurationSuggestions: () => axios.get(`${API}/ai-advanced/curation/suggestions`, { headers: getAuthHeader() }),
  analyzeMemory: (memoryId) => axios.post(`${API}/ai-advanced/curation/analyze/${memoryId}`, {}, { headers: getAuthHeader() }),
  
  // Semantic Search
  advancedSearch: (query, method = 'hybrid', limit = 20, minScore = 0.5) => axios.post(`${API}/ai-advanced/search`, 
    { query, method, limit, min_score: minScore }, { headers: getAuthHeader() }),
  embedAllMemories: () => axios.post(`${API}/ai-advanced/search/embed-all`, {}, { headers: getAuthHeader() }),
  getEmbeddingStatus: () => axios.get(`${API}/ai-advanced/search/status`, { headers: getAuthHeader() }),
  
  // Conversational Exploration
  exploreMemories: (message, conversationId, includeVisuals = true) => axios.post(`${API}/ai-advanced/explore`, 
    { message, conversation_id: conversationId, include_visuals: includeVisuals }, { headers: getAuthHeader() }),
  
  // Recommendations
  getRecommendations: (limit = 5) => axios.get(`${API}/ai-advanced/recommendations`, 
    { headers: getAuthHeader(), params: { limit } }),
  
  // Reports
  getYearlyReport: (year) => axios.get(`${API}/ai-advanced/reports/yearly/${year}`, { headers: getAuthHeader() }),
  getLifetimeReport: () => axios.get(`${API}/ai-advanced/reports/lifetime`, { headers: getAuthHeader() }),

  // ============================================
  // BOOK GENERATOR APIs (Phase 2)
  // ============================================
  
  // Book Generation
  generateBookFromEvent: (eventId, theme = 'modern_minimal', includeCaptions = true) => 
    axios.post(`${API}/book-generator/from-event`, { event_id: eventId, theme, include_captions: includeCaptions }, { headers: getAuthHeader() }),
  generateBookFromMemories: (memoryIds, title, theme = 'modern_minimal', includeCaptions = true) => 
    axios.post(`${API}/book-generator/from-memories`, { memory_ids: memoryIds, title, theme, include_captions: includeCaptions }, { headers: getAuthHeader() }),
  generateYearlyBook: (year, theme = 'modern_minimal') => 
    axios.post(`${API}/book-generator/yearly`, { year, theme }, { headers: getAuthHeader() }),
  
  // Book Preview & Pages
  getBookPages: (bookId) => axios.get(`${API}/book-generator/${bookId}/pages`, { headers: getAuthHeader() }),
  getBookPreview: (bookId) => axios.get(`${API}/book-generator/preview/${bookId}`, { headers: getAuthHeader() }),
  updateBookPage: (bookId, pageId, layout, content) => 
    axios.put(`${API}/book-generator/${bookId}/pages`, { page_id: pageId, layout, content }, { headers: getAuthHeader() }),
  reorderBookPages: (bookId, pageOrder) => 
    axios.put(`${API}/book-generator/${bookId}/reorder`, { page_order: pageOrder }, { headers: getAuthHeader() }),
  updateBookTheme: (bookId, theme) => 
    axios.put(`${API}/book-generator/${bookId}/theme?theme=${theme}`, {}, { headers: getAuthHeader() }),
  regenerateCaption: (bookId, memoryId) => 
    axios.post(`${API}/book-generator/${bookId}/regenerate-caption`, { memory_id: memoryId }, { headers: getAuthHeader() }),
  
  // Configuration
  getBookThemes: () => axios.get(`${API}/book-generator/themes`, { headers: getAuthHeader() }),
  getPageLayouts: () => axios.get(`${API}/book-generator/layouts`, { headers: getAuthHeader() }),
  getBookSuggestions: () => axios.get(`${API}/book-generator/suggestions`, { headers: getAuthHeader() }),

  // ============================================
  // CINEMATIC BOOK ENGINE APIs
  // ============================================
  generateCinematicBook: (sourceType, sourceParams, style) =>
    axios.post(`${API}/book-generator/cinematic/generate`, { source_type: sourceType, source_params: sourceParams, style }, { headers: getAuthHeader() }),
  generateStoryEngineBook: (sourceType, sourceParams, style, tone) =>
    axios.post(`${API}/book-generator/story-engine/generate`, { source_type: sourceType, source_params: sourceParams, style, tone }, { headers: getAuthHeader() }),
  getCinematicThemes: () => axios.get(`${API}/book-generator/cinematic/themes`, { headers: getAuthHeader() }),
  getCinematicCoverTypes: () => axios.get(`${API}/book-generator/cinematic/cover-types`, { headers: getAuthHeader() }),
  getBookSources: () => axios.get(`${API}/book-generator/cinematic/sources`, { headers: getAuthHeader() }),
  applyBookStyle: (bookId, style) =>
    axios.post(`${API}/book-generator/${bookId}/apply-style`, { style }, { headers: getAuthHeader() }),
  deleteBookPage: (bookId, pageId) =>
    axios.delete(`${API}/book-generator/${bookId}/pages/${pageId}`, { headers: getAuthHeader() }),
  updateChapterTitle: (bookId, pageId, newTitle) =>
    axios.put(`${API}/book-generator/${bookId}/chapter-title`, { page_id: pageId, new_title: newTitle }, { headers: getAuthHeader() }),
  updatePageCaption: (bookId, pageId, caption) =>
    axios.put(`${API}/book-generator/${bookId}/caption`, { page_id: pageId, caption }, { headers: getAuthHeader() }),

  // ============================================
  // PRINT SERVICE APIs (Blurb Integration)
  // ============================================
  
  // Print Options & Pricing
  getPrintOptions: () => axios.get(`${API}/print/options`, { headers: getAuthHeader() }),
  getPrintProducts: () => axios.get(`${API}/print/products`, { headers: getAuthHeader() }),
  calculatePrintPricing: (config) => axios.post(`${API}/print/pricing`, config, { headers: getAuthHeader() }),
  getBookPricing: (bookId, params) => axios.get(`${API}/print/pricing/book/${bookId}`, { headers: getAuthHeader(), params }),
  
  // Print Orders
  createPrintOrder: (orderData) => axios.post(`${API}/print/orders`, orderData, { headers: getAuthHeader() }),
  createPrintPaymentIntent: (orderData) => axios.post(`${API}/print/payment-intent`, orderData, { headers: getAuthHeader() }),
  getPrintOrders: (limit = 20) => axios.get(`${API}/print/orders`, { headers: getAuthHeader(), params: { limit } }),
  getPrintOrder: (orderId) => axios.get(`${API}/print/orders/${orderId}`, { headers: getAuthHeader() }),
  refreshOrderStatus: (orderId) => axios.post(`${API}/print/orders/${orderId}/refresh-status`, {}, { headers: getAuthHeader() }),

  // Admin printed-book product pricing
  adminGetPrintProductPricing: (token) => axios.get(`${API}/admin/product-pricing/print-products`, { headers: { Authorization: `Bearer ${token}` } }),
  adminUpdatePrintProductPricing: (token, variantId, data) => axios.put(`${API}/admin/product-pricing/print-products/${encodeURIComponent(variantId)}`, data, { headers: { Authorization: `Bearer ${token}` } }),
  
  // Book Printability
  checkBookPrintable: (bookId) => axios.get(`${API}/print/books/${bookId}/printable`, { headers: getAuthHeader() }),
  getPrintableBooks: () => axios.get(`${API}/print/books/ready`, { headers: getAuthHeader() }),

  // ============================================
  // DOCUMENTARY ENGINE APIs (Sora 2 Video Generation)
  // ============================================
  
  // Documentary Options & Creation
  getDocumentaryOptions: () => axios.get(`${API}/documentary/options`, { headers: getAuthHeader() }),
  getDocumentarySuggestions: () => axios.get(`${API}/documentary/suggestions`, { headers: getAuthHeader() }),
  getDocumentarySources: () => axios.get(`${API}/documentary/sources`, { headers: getAuthHeader() }),
  previewDocumentaryStructure: (config) => axios.post(`${API}/documentary/preview-structure`, config, { headers: getAuthHeader() }),
  createDocumentary: (config) => axios.post(`${API}/documentary/create`, config, { headers: getAuthHeader() }),
  
  // Documentary Management
  listDocumentaries: (limit = 20) => axios.get(`${API}/documentary/list`, { headers: getAuthHeader(), params: { limit } }),
  getDocumentary: (docId) => axios.get(`${API}/documentary/${docId}`, { headers: getAuthHeader() }),
  deleteDocumentary: (docId) => axios.delete(`${API}/documentary/${docId}`, { headers: getAuthHeader() }),
  
  // Video Generation
  generateScene: (docId, sceneIndex) => axios.post(`${API}/documentary/${docId}/generate-scene`, { scene_index: sceneIndex }, { headers: getAuthHeader() }),
  generateAllScenes: (docId) => axios.post(`${API}/documentary/${docId}/generate-all`, {}, { headers: getAuthHeader() }),
  forceRestartGeneration: (docId) => axios.post(`${API}/documentary/${docId}/force-restart`, {}, { headers: getAuthHeader() }),

  // Documentary Rendering
  renderDocumentary: (docId, format = 'landscape') => axios.post(`${API}/documentary/${docId}/render`, { output_format: format }, { headers: getAuthHeader() }),
  renderDocumentarySync: (docId, format = 'landscape') => axios.post(`${API}/documentary/${docId}/render-sync`, { output_format: format }, { headers: getAuthHeader() }),
  listDocumentaryRenders: (docId) => axios.get(`${API}/documentary/${docId}/renders`, { headers: getAuthHeader() }),
  
  // Documentary Sharing
  toggleDocumentaryShare: (docId) => axios.post(`${API}/documentary/${docId}/share`, {}, { headers: getAuthHeader() }),
  getDocumentaryShareStatus: (docId) => axios.get(`${API}/documentary/${docId}/share-status`, { headers: getAuthHeader() }),
  getSharedDocumentary: (shareToken) => axios.get(`${API}/documentary/shared/${shareToken}`),

  // ============== Payments ==============
  
  // Get pricing packages
  getPaymentPackages: () => axios.get(`${API}/payments/packages`, { headers: getAuthHeader() }),
  
  // Create checkout session for documentary
  createDocumentaryCheckout: (data) => axios.post(`${API}/payments/checkout/create`, data, { headers: getAuthHeader() }),
  
  // Check checkout status
  getCheckoutStatus: (sessionId) => axios.get(`${API}/payments/checkout/status/${sessionId}`, { headers: getAuthHeader() }),
  
  // Check if documentary is paid
  getDocumentaryPaymentStatus: (docId) => axios.get(`${API}/payments/documentary/${docId}/status`, { headers: getAuthHeader() }),

  // ============== Creation Wizard ==============
  
  // Config & Data
  getCreationConfig: () => axios.get(`${API}/creation/config`, { headers: getAuthHeader() }),
  getCreationPhotos: (params) => axios.get(`${API}/creation/photos`, { params, headers: getAuthHeader() }),
  getCreationEvents: () => axios.get(`${API}/creation/events`, { headers: getAuthHeader() }),
  getCreationSuggestions: () => axios.get(`${API}/creation/ai-suggestions`, { headers: getAuthHeader() }),
  
  // Wizard Flow
  startCreation: (data) => axios.post(`${API}/creation/start`, data, { headers: getAuthHeader() }),
  selectMemories: (data) => axios.post(`${API}/creation/select-memories`, data, { headers: getAuthHeader() }),
  selectStyle: (data) => axios.post(`${API}/creation/select-style`, data, { headers: getAuthHeader() }),
  startGeneration: (data) => axios.post(`${API}/creation/generate`, data, { headers: getAuthHeader() }),
  getCreationStatus: (id) => axios.get(`${API}/creation/${id}`, { headers: getAuthHeader() }),
  selectProduct: (data) => axios.post(`${API}/creation/select-product`, data, { headers: getAuthHeader() }),
  customizeCreation: (data) => axios.post(`${API}/creation/customize`, data, { headers: getAuthHeader() }),
  createCreationCheckout: (id, origin) => axios.post(`${API}/creation/${id}/checkout?origin_url=${encodeURIComponent(origin)}`, {}, { headers: getAuthHeader() }),
  completeCreation: (id, sessionId) => axios.post(`${API}/creation/${id}/complete?session_id=${sessionId}`, {}, { headers: getAuthHeader() }),

  // ============== Memory Concierge ==============
  
  getConciergeSuggestions: (limit = 6) => axios.get(`${API}/concierge/suggestions`, { params: { limit }, headers: getAuthHeader() }),
  getConciergeSuggestionDetail: (id) => axios.get(`${API}/concierge/suggestions/${id}`, { headers: getAuthHeader() }),
  trackConciergeInteraction: (id, data) => axios.post(`${API}/concierge/suggestions/${id}/interact`, data, { headers: getAuthHeader() }),
  createFromSuggestion: (id, data) => axios.post(`${API}/concierge/suggestions/${id}/create`, data, { headers: getAuthHeader() }),
  refreshConciergeSuggestions: () => axios.post(`${API}/concierge/refresh`, {}, { headers: getAuthHeader() }),
  getConciergeStats: () => axios.get(`${API}/concierge/stats`, { headers: getAuthHeader() }),

  // ============== Notifications ==============
  getNotifications: (params) => axios.get(`${API}/notifications`, { headers: getAuthHeader(), params }),
  getUnreadCount: () => axios.get(`${API}/notifications/unread-count`, { headers: getAuthHeader() }),
  markNotificationRead: (id) => axios.post(`${API}/notifications/${id}/read`, {}, { headers: getAuthHeader() }),
  markAllNotificationsRead: () => axios.post(`${API}/notifications/read-all`, {}, { headers: getAuthHeader() }),
  deleteNotification: (id) => axios.delete(`${API}/notifications/${id}`, { headers: getAuthHeader() }),
  getNotificationPreferences: () => axios.get(`${API}/notifications/preferences`, { headers: getAuthHeader() }).then(res => res.data),
  updateNotificationPreferences: (data) => axios.put(`${API}/notifications/preferences`, data, { headers: getAuthHeader() }),
  checkAnniversaries: () => axios.post(`${API}/notifications/check-anniversaries`, {}, { headers: getAuthHeader() }),

  // ============== People (Face Recognition) ==============
  getPeople: (params) => axios.get(`${API}/people`, { headers: getAuthHeader(), params }),
  searchPeople: (query) => axios.get(`${API}/people/search`, { headers: getAuthHeader(), params: { q: query } }),
  getPerson: (id) => axios.get(`${API}/people/${id}`, { headers: getAuthHeader() }),
  getPersonPhotos: (id, params) => axios.get(`${API}/people/${id}/photos`, { headers: getAuthHeader(), params }),
  syncPersonPhotos: (id) => axios.post(`${API}/people/${id}/sync-photos`, null, { headers: getAuthHeader() }),
  getPersonStory: (id) => axios.get(`${API}/people/${id}/story`, { headers: getAuthHeader() }),
  getPersonInsights: (id) => axios.get(`${API}/people/${id}/insights`, { headers: getAuthHeader() }),
  getPersonTimeline: (id) => axios.get(`${API}/people/${id}/timeline`, { headers: getAuthHeader() }),
  updatePerson: (id, data) => axios.put(`${API}/people/${id}`, data, { headers: getAuthHeader() }),
  createPerson: (data) => axios.post(`${API}/people`, data, { headers: getAuthHeader() }),
  deletePerson: (id) => axios.delete(`${API}/people/${id}`, { headers: getAuthHeader() }),
  ignorePerson: (id) => axios.post(`${API}/people/${id}/ignore`, {}, { headers: getAuthHeader() }),
  mergePeople: (data) => axios.post(`${API}/people/merge`, data, { headers: getAuthHeader() }),
  assignPhotosToPersonn: (id, data) => axios.post(`${API}/people/${id}/photos`, data, { headers: getAuthHeader() }),
  removePhotoFromPerson: (personId, memoryId) => axios.delete(`${API}/people/${personId}/photos/${memoryId}`, { headers: getAuthHeader() }),
  triggerFaceAnalysis: (limit = 50, rescan = false) => axios.post(`${API}/people/analyze`, {}, { headers: getAuthHeader(), params: { limit, rescan } }),
  analyzeSinglePhoto: (memoryId) => axios.post(`${API}/people/analyze/${memoryId}`, {}, { headers: getAuthHeader() }),
  getFaceGroupingSuggestions: (limit = 10) => axios.get(`${API}/people/suggestions/groupings`, { headers: getAuthHeader(), params: { limit } }),
  getPhotosWithPeople: (personIds) => axios.get(`${API}/people/photos-with`, { headers: getAuthHeader(), params: { person_ids: personIds } }),
  getPeopleConfig: () => axios.get(`${API}/people/config`, { headers: getAuthHeader() }),
  getPeopleStats: () => axios.get(`${API}/people/stats`, { headers: getAuthHeader() }),
  getReviewSuggestions: () => axios.get(`${API}/people/review-suggestions`, { headers: getAuthHeader() }),
  resolveReviewSuggestion: (data) => axios.post(`${API}/people/review-suggestions/resolve`, data, { headers: getAuthHeader() }),
  getMoveTargets: (excludeId, q) => axios.get(`${API}/people/move-targets`, { headers: getAuthHeader(), params: { exclude_id: excludeId, q: q || undefined } }),
  getRelationshipProfiles: () => axios.get(`${API}/people/relationships`, { headers: getAuthHeader() }),
  getDuplicateCandidates: () => axios.get(`${API}/people/duplicate-candidates`, { headers: getAuthHeader() }),
  reprocessPeopleLibrary: () => axios.post(`${API}/people/reprocess`, {}, { headers: getAuthHeader() }),
  repairIdentityData: () => axios.post(`${API}/people/repair`, {}, { headers: getAuthHeader() }),
  adminRepairIdentity: (adminToken) => axios.post(`${API}/admin/people/repair`, {}, { headers: { Authorization: `Bearer ${adminToken}` } }),
  rebuildPeopleStats: () => axios.post(`${API}/people/rebuild-stats`, {}, { headers: getAuthHeader() }),
  getScanStatus: () => axios.get(`${API}/people/scan-status`, { headers: getAuthHeader() }),
  getReviewQueue: () => axios.get(`${API}/people/review-queue`, { headers: getAuthHeader() }),
  getIdentityAssist: (id) => axios.get(`${API}/people/${id}/identity-assist`, { headers: getAuthHeader() }),
  confirmPerson: (id, data) => axios.post(`${API}/people/${id}/confirm`, data, { headers: getAuthHeader() }),

  // Face Reassignment
  reassignFace: (data) => axios.post(`${API}/people/reassign-face`, data, { headers: getAuthHeader() }),
  removeFace: (data) => axios.post(`${API}/people/remove-face`, data, { headers: getAuthHeader() }),
  bulkReassignFaces: (data) => axios.post(`${API}/people/bulk-reassign`, data, { headers: getAuthHeader() }),
  createPersonFromFaces: (data) => axios.post(`${API}/people/create-from-faces`, data, { headers: getAuthHeader() }),
  recalculateImportance: () => axios.post(`${API}/people/recalculate-importance`, {}, { headers: getAuthHeader() }),
  getPersonImportance: (id) => axios.get(`${API}/people/${id}/importance`, { headers: getAuthHeader() }),
  arcfaceReprocess: (limit = 100) => axios.post(`${API}/people/arcface-reprocess`, {}, { headers: getAuthHeader(), params: { limit } }),
  arcfaceStatus: () => axios.get(`${API}/people/arcface-status`, { headers: getAuthHeader() }),

  // ============== Viral Growth Engine ==============
  
  // Shareable Memory Pages
  createMemoryShareLink: (data) => axios.post(`${API}/viral/share/memory`, data, { headers: getAuthHeader() }),
  getSharedMemory: (slug) => axios.get(`${API}/viral/memory/${slug}`),
  getSharedMemoryPhotos: (slug, offset = 0, limit = 20) => axios.get(`${API}/viral/memory/${slug}/photos`, { params: { offset, limit } }),
  getMyShareLinks: () => axios.get(`${API}/viral/my-shares`, { headers: getAuthHeader() }),
  deleteShareLink: (shareId) => axios.delete(`${API}/viral/share/${shareId}`, { headers: getAuthHeader() }),
  toggleShareContributions: (shareId) => axios.patch(`${API}/viral/share/${shareId}/toggle-contributions`, {}, { headers: getAuthHeader() }),
  
  // Collaborative Contributions
  contributeToMemory: (slug, data) => axios.post(`${API}/viral/memory/${slug}/contribute`, data, { headers: getAuthHeader() }),
  getMemoryContributors: (slug) => axios.get(`${API}/viral/memory/${slug}/contributors`),
  
  // Yearly Reports
  generateYearlyReport: (data) => axios.post(`${API}/viral/year/generate`, data, { headers: getAuthHeader() }),
  getMyYearlyReport: (year) => axios.get(`${API}/viral/year/${year}`, { headers: getAuthHeader() }),
  getPublicYearlyReport: (year, username) => axios.get(`${API}/viral/year/${year}/${username}`),

  // ============== Camera ("Remember This") ==============
  getCameraConfig: () => axios.get(`${API}/camera/config`, { headers: getAuthHeader() }),
  getCameraFilters: () => axios.get(`${API}/camera/filters`, { headers: getAuthHeader() }),
  getCinematicFilters: () => axios.get(`${API}/camera/cinematic-filters`),
  getCinematicFilterDetail: (filterId) => axios.get(`${API}/camera/cinematic-filters/${filterId}`),
  getCinematicFilterCanvas: (filterId, intensity = 100) => axios.get(`${API}/camera/cinematic-filters/${filterId}/canvas`, { params: { intensity } }),

  // My Looks
  getMyLooks: () => axios.get(`${API}/camera/my-looks`, { headers: getAuthHeader() }),
  createLook: (data) => axios.post(`${API}/camera/my-looks`, data, { headers: getAuthHeader() }),
  renameLook: (lookId, name) => axios.put(`${API}/camera/my-looks/${lookId}`, { name }, { headers: getAuthHeader() }),
  deleteLook: (lookId) => axios.delete(`${API}/camera/my-looks/${lookId}`, { headers: getAuthHeader() }),

  // AI Image Editing
  getAIEditPresets: () => axios.get(`${API}/camera/ai-edit/presets`, { headers: getAuthHeader() }),
  aiEditImage: (data) => axios.post(`${API}/camera/ai-edit`, data, { headers: getAuthHeader(), timeout: 120000 }),

  capturePhoto: (data) => axios.post(`${API}/camera/capture`, data, { headers: getAuthHeader() }),
  captureFromUrl: (url, type = 'photo') => axios.post(`${API}/camera/capture/url`, { url, capture_type: type }, { headers: getAuthHeader() }),
  suggestFilter: (imageData) => axios.post(`${API}/camera/suggest-filter`, { image_data: imageData }, { headers: getAuthHeader() }),
  getCaptures: (params) => axios.get(`${API}/camera/captures`, { headers: getAuthHeader(), params }),
  getCapture: (id) => axios.get(`${API}/camera/captures/${id}`, { headers: getAuthHeader() }),
  updateCapture: (id, data) => axios.put(`${API}/camera/captures/${id}`, data, { headers: getAuthHeader() }),
  deleteCapture: (id) => axios.delete(`${API}/camera/captures/${id}`, { headers: getAuthHeader() }),
  processCapture: (id) => axios.post(`${API}/camera/captures/${id}/process`, {}, { headers: getAuthHeader() }),
  processCapturesBatch: (captureIds) => axios.post(`${API}/camera/process-batch`, { capture_ids: captureIds }, { headers: getAuthHeader() }),
  getMemorySuggestions: (hoursBack = 24) => axios.get(`${API}/camera/suggestions`, { headers: getAuthHeader(), params: { hours_back: hoursBack } }),
  createMemoryFromCaptures: (data) => axios.post(`${API}/camera/create-memory`, data, { headers: getAuthHeader() }),
  getCameraTimeline: (year, month) => axios.get(`${API}/camera/timeline/${year}`, { headers: getAuthHeader(), params: month ? { month } : {} }),
  getCapturesForDate: (date) => axios.get(`${API}/camera/timeline/date/${date}`, { headers: getAuthHeader() }),
  getCameraStats: () => axios.get(`${API}/camera/stats`, { headers: getAuthHeader() }),
  getCameraLocationPreference: () => axios.get(`${API}/camera/location-preference`, { headers: getAuthHeader() }),
  updateCameraLocationPreference: (enabled) => axios.put(`${API}/camera/location-preference`, null, { headers: getAuthHeader(), params: { enabled } }),

  // ============== Albums ==============
  createAlbum: (data) => axios.post(`${API}/albums`, data, { headers: getAuthHeader() }),
  getAlbums: () => axios.get(`${API}/albums`, { headers: getAuthHeader() }),
  getRecentAlbums: (limit = 5) => axios.get(`${API}/albums/recent`, { headers: getAuthHeader(), params: { limit } }),
  getAlbum: (id) => axios.get(`${API}/albums/${id}`, { headers: getAuthHeader() }),
  updateAlbum: (id, data) => axios.put(`${API}/albums/${id}`, data, { headers: getAuthHeader() }),
  deleteAlbum: (id) => axios.delete(`${API}/albums/${id}`, { headers: getAuthHeader() }),
  shareAlbum: (id) => axios.post(`${API}/albums/${id}/share`, {}, { headers: getAuthHeader() }),
  getPublicAlbum: (slug) => axios.get(`${API}/albums/public/${slug}`),
  getPublicAlbumGallery: (slug, params = {}) => axios.get(`${API}/albums/public/${slug}/gallery`, { params }),
  getPublicAlbumMedia: (slug, memoryId) => axios.get(`${API}/albums/public/${slug}/media/${memoryId}`),
  claimAlbumInvitation: (slug) => axios.post(`${API}/albums/public/${slug}/claim`, {}, { headers: getAuthHeader() }),
  publicUpload: (slug, formData, config = {}) => axios.post(`${API}/albums/public/${slug}/upload`, formData, {
    ...config,
    headers: { ...(config.headers || {}), 'Content-Type': 'multipart/form-data' }
  }),
  getContributionClaimStatus: (claimToken) => axios.post(`${API}/albums/contributions/claim/status`, {
    claim_token: claimToken
  }, { headers: getAuthHeader() }),
  claimAlbumContribution: (claimToken, authFlow = '') => axios.post(`${API}/albums/contributions/claim`, {
    claim_token: claimToken,
    auth_flow: authFlow
  }, { headers: getAuthHeader() }),
  getAlbumQrCode: (slug) => `${API}/albums/qrcode/${slug}`,

  // ============== On This Day ==============
  getOnThisDay: (includeRange = 0) => axios.get(`${API}/on-this-day`, { headers: getAuthHeader(), params: { include_range: includeRange } }),
  getOnThisDaySummary: () => axios.get(`${API}/on-this-day/summary`, { headers: getAuthHeader() }),
  getOnThisDayForDate: (date) => axios.get(`${API}/on-this-day/date/${date}`, { headers: getAuthHeader() }),
  getUpcomingAnniversaries: (days = 7) => axios.get(`${API}/on-this-day/upcoming`, { headers: getAuthHeader(), params: { days } }),
  checkOnThisDay: () => axios.post(`${API}/on-this-day/check`, {}, { headers: getAuthHeader() }),
  getOnThisDayStory: (memoryId) => axios.get(`${API}/on-this-day/story/${memoryId}`, { headers: getAuthHeader() }),

  // ============== Story Wall (Public Feed) ==============
  // Public endpoints (no auth required)
  getStoryWall: (params) => axios.get(`${API}/social-feed`, { params }),
  getStoryWallHero: () => axios.get(`${API}/social-feed/hero`),
  getStoryWallStats: () => axios.get(`${API}/social-feed/stats`),
  getStoryWallCategories: () => axios.get(`${API}/social-feed/categories`),
  getStoryWallPost: (postId) => axios.get(`${API}/social-feed/post/${postId}`),
  getStoryWallPostRelated: (postId, limit = 6) => axios.get(`${API}/social-feed/post/${postId}/related`, { params: { limit } }),
  searchStoryWall: (q, limit = 20) => axios.get(`${API}/social-feed/search`, { params: { q, limit } }),
  getStoryWallByCategory: (category, params) => axios.get(`${API}/social-feed/category/${category}`, { params }),
  getFeaturedStories: (limit = 10) => axios.get(`${API}/social-feed/featured`, { params: { limit } }),
  getUserPublicStories: (userId) => axios.get(`${API}/social-feed/user/${userId}`),
  
  // Authenticated endpoints
  shareToStoryWall: (data) => axios.post(`${API}/social-feed/share`, data, { headers: getAuthHeader() }),
  removeFromStoryWall: (postId) => axios.delete(`${API}/social-feed/${postId}`, { headers: getAuthHeader() }),
  appreciateStory: (postId) => axios.post(`${API}/social-feed/post/${postId}/appreciate`, {}, { headers: getAuthHeader() }),
  getMyStoryWallPosts: () => axios.get(`${API}/social-feed/my-posts`, { headers: getAuthHeader() }),
  getMyShareableContent: () => axios.get(`${API}/social-feed/my-shareable`, { headers: getAuthHeader() }),
  checkFeaturedStatus: () => axios.get(`${API}/social-feed/am-i-featured`, { headers: getAuthHeader() }),

  // Legacy aliases (backward compat)
  getFeed: (params) => axios.get(`${API}/social-feed`, { params }),
  getFeedHero: () => axios.get(`${API}/social-feed/hero`),
  getFeedStats: () => axios.get(`${API}/social-feed/stats`),
  getFeedCategories: () => axios.get(`${API}/social-feed/categories`),
  getFeedPost: (postId) => axios.get(`${API}/social-feed/post/${postId}`),
  getFeedPostRelated: (postId, limit = 6) => axios.get(`${API}/social-feed/post/${postId}/related`, { params: { limit } }),
  searchFeed: (q, limit = 20) => axios.get(`${API}/social-feed/search`, { params: { q, limit } }),
  getFeedByCategory: (category, params) => axios.get(`${API}/social-feed/category/${category}`, { params }),
  getFeaturedPosts: (limit = 10) => axios.get(`${API}/social-feed/featured`, { params: { limit } }),
  getUserPublicPosts: (userId) => axios.get(`${API}/social-feed/user/${userId}`),
  shareToFeed: (data) => axios.post(`${API}/social-feed/share`, data, { headers: getAuthHeader() }),
  removeFromFeed: (postId) => axios.delete(`${API}/social-feed/${postId}`, { headers: getAuthHeader() }),
  appreciatePost: (postId) => axios.post(`${API}/social-feed/post/${postId}/appreciate`, {}, { headers: getAuthHeader() }),
  getMyFeedPosts: () => axios.get(`${API}/social-feed/my-posts`, { headers: getAuthHeader() }),

  // ============== Content Moderation ==============
  scanContent: (data) => axios.post(`${API}/moderation/scan`, data, { headers: getAuthHeader() }),
  reportPost: (data) => axios.post(`${API}/moderation/report`, data, { headers: getAuthHeader() }),
  getReportCategories: () => axios.get(`${API}/moderation/report-categories`),
  blockUser: (targetUserId) => axios.post(`${API}/moderation/block-user`, { target_user_id: targetUserId }, { headers: getAuthHeader() }),
  muteUser: (targetUserId) => axios.post(`${API}/moderation/mute-user`, { target_user_id: targetUserId }, { headers: getAuthHeader() }),
  hidePost: (postId) => axios.post(`${API}/moderation/hide-post`, { post_id: postId }, { headers: getAuthHeader() }),
  // Admin moderation
  adminGetModerationQueue: (token, params) => axios.get(`${API}/moderation/admin/queue`, { headers: { Authorization: `Bearer ${token}` }, params }),
  adminGetReportsQueue: (token, params) => axios.get(`${API}/moderation/admin/reports`, { headers: { Authorization: `Bearer ${token}` }, params }),
  adminReviewPost: (token, postId, data) => axios.post(`${API}/moderation/admin/review/${postId}`, data, { headers: { Authorization: `Bearer ${token}` } }),
  adminGetModerationStats: (token) => axios.get(`${API}/moderation/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
  adminGetModerationLog: (token, params) => axios.get(`${API}/moderation/admin/log`, { headers: { Authorization: `Bearer ${token}` }, params }),
  adminGetDecisionLog: (token, params) => axios.get(`${API}/moderation/admin/decisions`, { headers: { Authorization: `Bearer ${token}` }, params }),

  // Today in Your Life
  getTodayMemories: (limit = 10) => axios.get(`${API}/today-in-life/memories`, { headers: getAuthHeader(), params: { limit } }),
  getAllTodayMemories: (limit = 50, offset = 0) => axios.get(`${API}/today-in-life/all`, { headers: getAuthHeader(), params: { limit, offset } }),
  createStoryFromToday: () => axios.post(`${API}/today-in-life/create-story`, {}, { headers: getAuthHeader() }),

  // AI Image Analysis
  analyzeImage: (memoryId) => axios.post(`${API}/ai/analyze`, { memory_id: memoryId }, { headers: getAuthHeader() }),
  batchAnalyzeImages: (memoryIds) => axios.post(`${API}/ai/analyze/batch`, { memory_ids: memoryIds }, { headers: getAuthHeader() }),
  getImageMetadata: (memoryId) => axios.get(`${API}/ai/metadata/${memoryId}`, { headers: getAuthHeader() }),
  getAnalysisStatus: (memoryId) => axios.get(`${API}/ai/status/${memoryId}`, { headers: getAuthHeader() }),

  // Face Library
  getUnknownFaces: (limit = 50) => axios.get(`${API}/ai/faces/unknown`, { headers: getAuthHeader(), params: { limit } }),
  nameFace: (faceId, name) => axios.post(`${API}/ai/faces/name`, { face_id: faceId, name }, { headers: getAuthHeader() }),
  linkFaceToPerson: (faceId, personId) => axios.post(`${API}/ai/faces/link`, { face_id: faceId, person_id: personId }, { headers: getAuthHeader() }),
  ignoreFace: (faceId) => axios.post(`${API}/ai/faces/${faceId}/ignore`, {}, { headers: getAuthHeader() }),
  createPerson: (name, faceId = null) => axios.post(`${API}/ai/people`, { name, face_id: faceId }, { headers: getAuthHeader() }),
  getPersonDetails: (personId) => axios.get(`${API}/ai/people/${personId}`, { headers: getAuthHeader() }),
  getPersonPhotos: (personId, limit = 50, offset = 0) => axios.get(`${API}/ai/people/${personId}/photos`, { headers: getAuthHeader(), params: { limit, offset } }),
  updatePerson: (personId, name) => axios.put(`${API}/ai/people/${personId}`, null, { headers: getAuthHeader(), params: { name } }),
  mergePeople: (sourceId, targetId) => axios.post(`${API}/ai/people/merge`, { source_person_id: sourceId, target_person_id: targetId }, { headers: getAuthHeader() }),
  deletePerson: (personId) => axios.delete(`${API}/ai/people/${personId}`, { headers: getAuthHeader() }),

  // ============== Life Movie ==============
  
  // Configuration
  getLifeMovieConfig: () => axios.get(`${API}/life-movie/config`, { headers: getAuthHeader() }),
  
  // CRUD
  createLifeMovie: (data) => axios.post(`${API}/life-movie/create`, data, { headers: getAuthHeader() }),
  listLifeMovies: (limit = 20) => axios.get(`${API}/life-movie/list`, { headers: getAuthHeader(), params: { limit } }),
  getLifeMovie: (movieId) => axios.get(`${API}/life-movie/${movieId}`, { headers: getAuthHeader() }),
  updateLifeMovie: (movieId, data) => axios.put(`${API}/life-movie/${movieId}`, data, { headers: getAuthHeader() }),
  deleteLifeMovie: (movieId) => axios.delete(`${API}/life-movie/${movieId}`, { headers: getAuthHeader() }),
  
  // Regeneration
  regenerateLifeMovie: (movieId) => axios.post(`${API}/life-movie/${movieId}/regenerate`, {}, { headers: getAuthHeader() }),
  
  // Audio
  getLifeMovieAudio: (movieId) => `${API}/life-movie/audio/${movieId}`,
  
  // Sharing
  shareLifeMovie: (movieId) => axios.post(`${API}/life-movie/${movieId}/share`, {}, { headers: getAuthHeader() }),
  getSharedLifeMovie: (shareToken) => axios.get(`${API}/life-movie/watch/${shareToken}`),
  getPublicLifeMovieAudio: (shareToken) => `${API}/life-movie/public/audio/${shareToken}`,
  
  // Featured Movies (Public)
  getFeaturedLifeMovies: (limit = 5) => axios.get(`${API}/life-movie/featured`, { params: { limit } }),

  // Cinematic Movie Engine
  generateCinematicMovie: (sourceType, sourceParams, music, voice, videoFormat, duration) =>
    axios.post(`${API}/life-movie/cinematic/generate`, {
      source_type: sourceType, source_params: sourceParams,
      music: music || 'emotional', voice: voice || 'nova',
      video_format: videoFormat || '16:9', duration_seconds: duration || 90
    }, { headers: getAuthHeader() }),
  generateDocumentary: (sourceType, sourceParams, music, voice, videoFormat, duration, tone) =>
    axios.post(`${API}/life-movie/documentary/generate`, {
      source_type: sourceType, source_params: sourceParams,
      music: music || 'emotional', voice: voice || 'nova',
      video_format: videoFormat || '16:9', duration_seconds: duration || 90,
      tone: tone || 'cinematic'
    }, { headers: getAuthHeader() }),
  getMovieSources: () => axios.get(`${API}/life-movie/cinematic/sources`, { headers: getAuthHeader() }),
  getMovieMusic: () => axios.get(`${API}/life-movie/cinematic/music`, { headers: getAuthHeader() }),
  getMovieFormats: () => axios.get(`${API}/life-movie/cinematic/formats`, { headers: getAuthHeader() }),
  getMovieVoices: () => axios.get(`${API}/life-movie/cinematic/voices`, { headers: getAuthHeader() }),
  deleteMovieScene: (movieId, sceneId) =>
    axios.delete(`${API}/life-movie/cinematic/${movieId}/scenes/${sceneId}`, { headers: getAuthHeader() }),
  updateMovieSceneTitle: (movieId, sceneId, newTitle) =>
    axios.put(`${API}/life-movie/cinematic/${movieId}/scene-title`, { scene_id: sceneId, new_title: newTitle }, { headers: getAuthHeader() }),
  updateMovieMusic: (movieId, music) =>
    axios.put(`${API}/life-movie/cinematic/${movieId}/music`, { music }, { headers: getAuthHeader() }),
  updateMovieStyle: (movieId, filterPreset) =>
    axios.put(`${API}/life-movie/cinematic/${movieId}/style`, { filter_preset: filterPreset }, { headers: getAuthHeader() }),
  generateMovieScript: (movieId) =>
    axios.post(`${API}/life-movie/cinematic/${movieId}/generate-script`, {}, { headers: getAuthHeader() }),
  reorderMovieScenes: (movieId, sceneOrder) =>
    axios.put(`${API}/life-movie/cinematic/${movieId}/reorder`, { scene_order: sceneOrder }, { headers: getAuthHeader() }),

  // ============== Billing & Subscriptions ==============
  
  // Pricing & Catalog
  getPricing: () => axios.get(`${API}/billing/pricing`),
  getBillingPlans: () => axios.get(`${API}/billing/plans`),
  getProducts: (category = null) => axios.get(`${API}/billing/products`, { params: category ? { category } : {} }),
  
  // Subscription
  getSubscription: () => axios.get(`${API}/billing/subscription`, { headers: getAuthHeader() }),
  upgradeSubscription: (data) => axios.post(`${API}/billing/subscription/upgrade`, data, { headers: getAuthHeader() }),
  cancelSubscription: () => axios.post(`${API}/billing/subscription/cancel`, {}, { headers: getAuthHeader() }),
  
  // Billing Portal
  createBillingPortal: (origin_url) => axios.post(`${API}/billing/portal`, { origin_url }, { headers: getAuthHeader() }),
  
  // Features / Gating
  getFeatures: () => axios.get(`${API}/billing/features`, { headers: getAuthHeader() }),
  
  // Usage
  getUsage: () => axios.get(`${API}/billing/usage`, { headers: getAuthHeader() }),
  checkUsageLimit: (feature) => axios.get(`${API}/billing/usage/check/${feature}`, { headers: getAuthHeader() }),
  
  // Checkout
  createCheckout: (data) => axios.post(`${API}/billing/checkout`, data, { headers: getAuthHeader() }),
  getCheckoutStatus: (sessionId) => axios.get(`${API}/billing/checkout/status/${sessionId}`, { headers: getAuthHeader() }),
  
  // Payments & Purchases
  getPayments: (limit = 50) => axios.get(`${API}/billing/payments`, { headers: getAuthHeader(), params: { limit } }),
  getPurchases: (category = null) => axios.get(`${API}/billing/purchases`, { headers: getAuthHeader(), params: category ? { category } : {} }),
  checkEntitlement: (productId) => axios.get(`${API}/billing/entitlement/${productId}`, { headers: getAuthHeader() }),
  
  // Gifting
  purchaseGift: (data) => axios.post(`${API}/billing/gift`, data, { headers: getAuthHeader() }),
  redeemGift: (code) => axios.post(`${API}/billing/gift/redeem`, { redemption_code: code }, { headers: getAuthHeader() }),
  
  // Family Plan
  createFamily: () => axios.post(`${API}/billing/family/create`, {}, { headers: getAuthHeader() }),
  joinFamily: (inviteCode) => axios.post(`${API}/billing/family/join`, { invite_code: inviteCode }, { headers: getAuthHeader() }),
  
  // Admin Stats
  getRevenueStats: (token, days = 30) => axios.get(`${API}/billing/admin/stats`, {
    headers: { Authorization: `Bearer ${token}` },
    params: { days }
  }),
  getAdminUserBilling: (token, userId) => axios.get(`${API}/billing/admin/user-billing/${userId}`, {
    headers: { Authorization: `Bearer ${token}` }
  }),

  // ============================================
  // PHOTO INTELLIGENCE APIs
  // ============================================
  
  // Analysis Status
  getMemoryAnalysisStatus: (memoryId) => axios.get(`${API}/memories/${memoryId}/analysis-status`, { headers: getAuthHeader() }),
  getAnalysisStats: () => axios.get(`${API}/memories/analysis-stats`, { headers: getAuthHeader() }),
  
  // Reprocessing
  reprocessMemory: (memoryId, options = {}) => axios.post(`${API}/memories/${memoryId}/reprocess`, options, { headers: getAuthHeader() }),
  reprocessBatch: (params = {}) => axios.post(`${API}/memories/reprocess-batch`, null, { headers: getAuthHeader(), params }),
  
  // ============================================
  // MEDIA ASSETS (P0 Foundation)
  // ============================================
  
  // Media Assets CRUD
  listMediaAssets: (params = {}) => axios.get(`${API}/media-assets`, { headers: getAuthHeader(), params }),
  getMediaAsset: (assetId) => axios.get(`${API}/media-assets/${assetId}`, { headers: getAuthHeader() }),
  deleteMediaAsset: (assetId) => axios.delete(`${API}/media-assets/${assetId}`, { headers: getAuthHeader() }),
  retryMediaProcessing: (assetId) => axios.post(`${API}/media-assets/${assetId}/retry`, {}, { headers: getAuthHeader() }),
  
  // Media Stats & Debug
  getMediaStats: () => axios.get(`${API}/media-assets/stats`, { headers: getAuthHeader() }),
  getMediaDebugPanel: (token, params = {}) => axios.get(`${API}/media-assets/admin/debug`, {
    headers: { Authorization: `Bearer ${token}` },
    params
  }),
  getStorageHealth: (token) => axios.get(`${API}/media-assets/admin/health`, {
    headers: { Authorization: `Bearer ${token}` }
  }),
  
  // Batch Operations
  retryAllFailedMedia: (limit = 50) => axios.post(`${API}/media-assets/batch/retry-failed`, null, { headers: getAuthHeader(), params: { limit } }),
  
  // Face Detection & Recognition
  getUnknownFaces: (limit = 50) => axios.get(`${API}/people/faces/unknown`, { headers: getAuthHeader(), params: { limit } }),
  getFaceClusters: () => axios.get(`${API}/people/faces/clusters`, { headers: getAuthHeader() }),
  assignFaceToPerson: (faceId, personId) => axios.post(`${API}/people/faces/${faceId}/assign`, { person_id: personId }, { headers: getAuthHeader() }),
  createPersonFromFace: (faceId, data) => axios.post(`${API}/people/faces/${faceId}/create-person`, data, { headers: getAuthHeader() }),
  
  // People
  getPeople: () => axios.get(`${API}/people`, { headers: getAuthHeader() }),
  getPerson: (personId) => axios.get(`${API}/people/${personId}`, { headers: getAuthHeader() }),
  updatePerson: (personId, data) => axios.put(`${API}/people/${personId}`, data, { headers: getAuthHeader() }),
  mergePeople: (primaryId, secondaryIds) => axios.post(`${API}/people/${primaryId}/merge`, { secondary_ids: secondaryIds }, { headers: getAuthHeader() }),
  
  // ============================================
  // PLACES (P1/P2)
  // ============================================
  
  getPlaces: (params = {}) => axios.get(`${API}/places`, { headers: getAuthHeader(), params }),
  getPlacesStats: () => axios.get(`${API}/places/stats`, { headers: getAuthHeader() }),
  getPlace: (placeId) => axios.get(`${API}/places/${placeId}`, { headers: getAuthHeader() }),
  getPlaceMedia: (placeId, params = {}) => axios.get(`${API}/places/${placeId}/media`, { headers: getAuthHeader(), params }),
  updatePlace: (placeId, data) => axios.put(`${API}/places/${placeId}`, data, { headers: getAuthHeader() }),
  deletePlace: (placeId) => axios.delete(`${API}/places/${placeId}`, { headers: getAuthHeader() }),
  
  // ============================================
  // MOMENTS (Auto-detected event groupings)
  // ============================================
  
  getMoments: (params = {}) => axios.get(`${API}/moments`, { headers: getAuthHeader(), params }),
  getMomentsStats: () => axios.get(`${API}/moments/stats`, { headers: getAuthHeader() }),
  getMoment: (momentId) => axios.get(`${API}/moments/${momentId}`, { headers: getAuthHeader() }),
  getMomentMedia: (momentId, params = {}) => axios.get(`${API}/moments/${momentId}/media`, { headers: getAuthHeader(), params }),
  createMoment: (data) => axios.post(`${API}/moments`, data, { headers: getAuthHeader() }),
  updateMoment: (momentId, data) => axios.put(`${API}/moments/${momentId}`, data, { headers: getAuthHeader() }),
  deleteMoment: (momentId) => axios.delete(`${API}/moments/${momentId}`, { headers: getAuthHeader() }),
  addMediaToMoment: (momentId, mediaIds) => axios.post(`${API}/moments/${momentId}/add-media`, { media_ids: mediaIds }, { headers: getAuthHeader() }),
  removeMediaFromMoment: (momentId, mediaIds) => axios.post(`${API}/moments/${momentId}/remove-media`, { media_ids: mediaIds }, { headers: getAuthHeader() }),
  detectMoments: (force = false) => axios.post(`${API}/moments/detect`, null, { headers: getAuthHeader(), params: { force } }),
  detectMomentsSync: (force = false) => axios.post(`${API}/moments/detect/sync`, null, { headers: getAuthHeader(), params: { force } }),
  
  // ============================================
  // LIFE SUMMARY (Command Center Widgets)
  // ============================================
  
  getThisWeekSummary: () => axios.get(`${API}/life-summary/this-week`, { headers: getAuthHeader() }),
  getThisMonthSummary: () => axios.get(`${API}/life-summary/this-month`, { headers: getAuthHeader() }),
  getLifeInsights: () => axios.get(`${API}/life-summary/insights`, { headers: getAuthHeader() }),
  getOnThisDay: (yearsBack = 10) => axios.get(`${API}/life-summary/on-this-day`, { headers: getAuthHeader(), params: { years_back: yearsBack } }),

  // ============================================
  // MEMORY MAP
  // ============================================
  
  getMapLocations: (qs = '') => axios.get(`${API}/memory-map/locations${qs}`, { headers: getAuthHeader() }),
  getMapStats: () => axios.get(`${API}/memory-map/stats`, { headers: getAuthHeader() }),
  seedDemoMap: () => axios.post(`${API}/memory-map/seed-demo`, {}, { headers: getAuthHeader() }),
  getMapPeopleFilter: () => axios.get(`${API}/memory-map/people-for-filter`, { headers: getAuthHeader() }),

  // Location Stories & Travel Journal
  getLocationClusters: () => axios.get(`${API}/location-stories/clusters`, { headers: getAuthHeader() }),
  getClusterDetail: (id) => axios.get(`${API}/location-stories/clusters/${id}`, { headers: getAuthHeader() }),
  generateLocationStory: (data) => axios.post(`${API}/location-stories/generate-story`, data, { headers: getAuthHeader() }),
  getTravelJournal: () => axios.get(`${API}/location-stories/travel-journal`, { headers: getAuthHeader() }),
  generateAllStories: () => axios.post(`${API}/location-stories/generate-all-stories`, {}, { headers: getAuthHeader() }),
  assignLocation: (data) => axios.post(`${API}/memory-map/assign-location`, data, { headers: getAuthHeader() }),

  // ============================================
  // AI STORY GENERATOR
  // ============================================
  
  generateStory: (data) => axios.post(`${API}/story-generator/generate`, data, { headers: getAuthHeader() }),
  getGeneratedStories: () => axios.get(`${API}/story-generator/stories`, { headers: getAuthHeader() }),
  getGeneratedStory: (id) => axios.get(`${API}/story-generator/stories/${id}`, { headers: getAuthHeader() }),
  deleteGeneratedStory: (id) => axios.delete(`${API}/story-generator/stories/${id}`, { headers: getAuthHeader() }),
  regenerateStory: (id) => axios.post(`${API}/story-generator/stories/${id}/regenerate`, {}, { headers: getAuthHeader() }),
  shareStory: (id) => axios.post(`${API}/story-generator/stories/${id}/share`, {}, { headers: getAuthHeader() }),
  unshareStory: (id) => axios.post(`${API}/story-generator/stories/${id}/unshare`, {}, { headers: getAuthHeader() }),
  getSharedStory: (token) => axios.get(`${API}/story-generator/shared/${token}`),
  getStoryNarrationTypes: () => axios.get(`${API}/story-generator/types`, { headers: getAuthHeader() }),
  getStoryTones: () => axios.get(`${API}/story-generator/tones`, { headers: getAuthHeader() }),
  getStoryPerspectives: () => axios.get(`${API}/story-generator/perspectives`, { headers: getAuthHeader() }),
  getStorySources: () => axios.get(`${API}/story-generator/sources`, { headers: getAuthHeader() }),
  convertStoryToChapter: (storyId, chapterTitle) => axios.post(`${API}/story-generator/stories/${storyId}/convert-to-chapter`, { chapter_title: chapterTitle }, { headers: getAuthHeader() }),
  getBookChapters: () => axios.get(`${API}/story-generator/book-chapters`, { headers: getAuthHeader() }),
  deleteBookChapter: (chapterId) => axios.delete(`${API}/story-generator/book-chapters/${chapterId}`, { headers: getAuthHeader() }),

  // Daily Memory Engine
  getTodayMemories: () => axios.get(`${API}/daily-memories/today`, { headers: getAuthHeader() }),
  getThisWeekMemories: () => axios.get(`${API}/daily-memories/this-week`, { headers: getAuthHeader() }),
  getDailyDigest: () => axios.get(`${API}/daily-memories/combined`, { headers: getAuthHeader() }),
  generateTodaysStory: () => axios.post(`${API}/daily-memories/todays-story/generate`, {}, { headers: getAuthHeader() }),
  getLatestTodaysStory: () => axios.get(`${API}/daily-memories/todays-story/latest`, { headers: getAuthHeader() }),
  getTodaysStory: (id) => axios.get(`${API}/daily-memories/todays-story/${id}`, { headers: getAuthHeader() }),
  listTodaysStories: () => axios.get(`${API}/daily-memories/todays-story/list/all`, { headers: getAuthHeader() }),
  deleteTodaysStory: (id) => axios.delete(`${API}/daily-memories/todays-story/${id}`, { headers: getAuthHeader() }),

  // Video Trimming
  probeVideo: (memoryId) => axios.get(`${API}/video-trim/probe/${memoryId}`, { headers: getAuthHeader() }),
  trimVideo: (data) => axios.post(`${API}/video-trim/trim`, data, { headers: getAuthHeader() }),
  getTrimHistory: () => axios.get(`${API}/video-trim/history`, { headers: getAuthHeader() }),
  deleteTrim: (trimId) => axios.delete(`${API}/video-trim/${trimId}`, { headers: getAuthHeader() }),

  // AI Chapter Suggestions
  getChapterSuggestions: () => axios.get(`${API}/chapter-suggestions/suggestions`, { headers: getAuthHeader() }),
  acceptChapterSuggestion: (suggestionId, title) => axios.post(`${API}/chapter-suggestions/accept`, { suggestion_id: suggestionId, title }, { headers: getAuthHeader() }),
  dismissChapterSuggestion: (suggestionId) => axios.post(`${API}/chapter-suggestions/dismiss`, { suggestion_id: suggestionId }, { headers: getAuthHeader() }),

  // Family Timelines
  createFamilyTimeline: (data) => axios.post(`${API}/family-timelines/create`, data, { headers: getAuthHeader() }),
  listFamilyTimelines: () => axios.get(`${API}/family-timelines/list`, { headers: getAuthHeader() }),
  getFamilyTimeline: (id) => axios.get(`${API}/family-timelines/${id}`, { headers: getAuthHeader() }),
  updateFamilyTimeline: (id, data) => axios.put(`${API}/family-timelines/${id}`, data, { headers: getAuthHeader() }),
  addFamilyMember: (timelineId, data) => axios.post(`${API}/family-timelines/${timelineId}/members`, data, { headers: getAuthHeader() }),
  removeFamilyMember: (timelineId, memberId) => axios.delete(`${API}/family-timelines/${timelineId}/members/${memberId}`, { headers: getAuthHeader() }),
  deleteFamilyTimeline: (id) => axios.delete(`${API}/family-timelines/${id}`, { headers: getAuthHeader() }),

  // ============================================
  // LIFE CIRCLES
  // ============================================

  getLifeCircles: () => axios.get(`${API}/life-circles`, { headers: getAuthHeader() }),
  createLifeCircle: (data) => axios.post(`${API}/life-circles`, data, { headers: getAuthHeader() }),
  getLifeCircle: (id) => axios.get(`${API}/life-circles/${id}`, { headers: getAuthHeader() }),
  updateLifeCircle: (id, data) => axios.put(`${API}/life-circles/${id}`, data, { headers: getAuthHeader() }),
  deleteLifeCircle: (id) => axios.delete(`${API}/life-circles/${id}`, { headers: getAuthHeader() }),
  getCircleMembers: (id) => axios.get(`${API}/life-circles/${id}/members`, { headers: getAuthHeader() }),
  inviteCircleMember: (id, data) => axios.post(`${API}/life-circles/${id}/members`, data, { headers: getAuthHeader() }),
  updateCircleMemberRole: (id, memberId, data) => axios.put(`${API}/life-circles/${id}/members/${memberId}`, data, { headers: getAuthHeader() }),
  removeCircleMember: (id, memberId) => axios.delete(`${API}/life-circles/${id}/members/${memberId}`, { headers: getAuthHeader() }),
  getCircleInviteLink: (id) => axios.get(`${API}/life-circles/${id}/invite-link`, { headers: getAuthHeader() }),
  joinCircle: (inviteCode) => axios.post(`${API}/life-circles/join/${inviteCode}`, {}, { headers: getAuthHeader() }),
  getCircleMemories: (id, params) => axios.get(`${API}/life-circles/${id}/memories`, { headers: getAuthHeader(), params }),
  addMemoriesToCircle: (id, memoryIds) => axios.post(`${API}/life-circles/${id}/memories`, { memory_ids: memoryIds }, { headers: getAuthHeader() }),
  removeMemoryFromCircle: (id, memoryId) => axios.delete(`${API}/life-circles/${id}/memories/${memoryId}`, { headers: getAuthHeader() }),
  getCircleTimeline: (id) => axios.get(`${API}/life-circles/${id}/timeline`, { headers: getAuthHeader() }),
  getCirclePhotos: (id) => axios.get(`${API}/life-circles/${id}/photos`, { headers: getAuthHeader() }),
  getCirclePeople: (id) => axios.get(`${API}/life-circles/${id}/people`, { headers: getAuthHeader() }),
  seedDemoCircles: () => axios.post(`${API}/life-circles/seed-demo`, {}, { headers: getAuthHeader() }),

  // ============================================
  // ONBOARDING
  // ============================================

  getOnboardingStatus: () => axios.get(`${API}/onboarding/status`, { headers: getAuthHeader() }),
  completeOnboarding: () => axios.post(`${API}/onboarding/complete`, {}, { headers: getAuthHeader() }),
  seedOnboardingDemo: () => axios.post(`${API}/onboarding/demo-seed`, {}, { headers: getAuthHeader() }),

  // ============================================
  // USER PROFILE
  // ============================================

  getUserProfile: () => axios.get(`${API}/user/profile`, { headers: getAuthHeader() }),
  updateUserProfile: (data) => axios.put(`${API}/user/profile`, data, { headers: getAuthHeader() }),

  // ============================================
  // PROJECTS
  // ============================================

  listProjects: () => axios.get(`${API}/projects`, { headers: getAuthHeader() }),
  createProject: (data) => axios.post(`${API}/projects`, data, { headers: getAuthHeader() }),
  getProject: (id) => axios.get(`${API}/projects/${id}`, { headers: getAuthHeader() }),
  updateProject: (id, data) => axios.put(`${API}/projects/${id}`, data, { headers: getAuthHeader() }),
  deleteProject: (id) => axios.delete(`${API}/projects/${id}`, { headers: getAuthHeader() }),

  // ============================================
  // LIFESTART (Child Timeline)
  // ============================================

  listChildProfiles: () => axios.get(`${API}/lifestart`, { headers: getAuthHeader() }),
  createChildProfile: (data) => axios.post(`${API}/lifestart`, data, { headers: getAuthHeader() }),
  getChildProfile: (id) => axios.get(`${API}/lifestart/${id}`, { headers: getAuthHeader() }),
  updateChildProfile: (id, data) => axios.put(`${API}/lifestart/${id}`, data, { headers: getAuthHeader() }),
  deleteChildProfile: (id) => axios.delete(`${API}/lifestart/${id}`, { headers: getAuthHeader() }),
  getChildStats: (id) => axios.get(`${API}/lifestart/${id}/stats`, { headers: getAuthHeader() }),
  getChildTimeline: (id, params) => axios.get(`${API}/lifestart/${id}/timeline`, { headers: getAuthHeader(), params }),
  // Child Memories
  listChildMemories: (id, params) => axios.get(`${API}/lifestart/${id}/memories`, { headers: getAuthHeader(), params }),
  createChildMemory: (id, data) => axios.post(`${API}/lifestart/${id}/memories`, data, { headers: getAuthHeader() }),
  uploadChildMemoryPhoto: (childId, formData) => axios.post(`${API}/lifestart/${childId}/memories/upload`, formData, { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }),
  deleteChildMemory: (childId, memId) => axios.delete(`${API}/lifestart/${childId}/memories/${memId}`, { headers: getAuthHeader() }),
  // Milestones
  listChildMilestones: (id) => axios.get(`${API}/lifestart/${id}/milestones`, { headers: getAuthHeader() }),
  createChildMilestone: (id, data) => axios.post(`${API}/lifestart/${id}/milestones`, data, { headers: getAuthHeader() }),
  deleteChildMilestone: (childId, msId) => axios.delete(`${API}/lifestart/${childId}/milestones/${msId}`, { headers: getAuthHeader() }),
  // Letters to the Future
  listChildLetters: (id) => axios.get(`${API}/lifestart/${id}/letters`, { headers: getAuthHeader() }),
  createChildLetter: (id, data) => axios.post(`${API}/lifestart/${id}/letters`, data, { headers: getAuthHeader() }),
  updateChildLetter: (childId, letterId, data) => axios.put(`${API}/lifestart/${childId}/letters/${letterId}`, data, { headers: getAuthHeader() }),
  deleteChildLetter: (childId, letterId) => axios.delete(`${API}/lifestart/${childId}/letters/${letterId}`, { headers: getAuthHeader() }),
  // Contributors
  listChildContributors: (id) => axios.get(`${API}/lifestart/${id}/contributors`, { headers: getAuthHeader() }),
  addChildContributor: (id, data) => axios.post(`${API}/lifestart/${id}/contributors`, data, { headers: getAuthHeader() }),
  removeChildContributor: (childId, contribId) => axios.delete(`${API}/lifestart/${childId}/contributors/${contribId}`, { headers: getAuthHeader() }),

  // Life Trailer
  generateTrailer: () => axios.post(`${API}/trailer/generate`, {}, { headers: getAuthHeader() }),
  getTrailers: () => axios.get(`${API}/trailer/list`, { headers: getAuthHeader() }),
  shareTrailer: (trailerId, privacy = 'public') => axios.post(`${API}/trailer/share`, { trailer_id: trailerId, privacy_setting: privacy }, { headers: getAuthHeader() }),
  getTrailerCaptions: (trailerId) => axios.post(`${API}/trailer/caption`, { trailer_id: trailerId }, { headers: getAuthHeader() }),
  getShareAnalytics: (slug) => axios.get(`${API}/trailer/share/${slug}/analytics`, { headers: getAuthHeader() }),
};
